import { supabase } from '@/lib/supabase'

export type BootPreset = 'quick'|'standard'|'max'

// SRP: serviços relacionados ao bootstrap/normalização inicial
export async function bootstrapFill5m(params: { symbol: string; target?: number; preset?: BootPreset; cap?: number }): Promise<number> {
  const s = params.symbol?.toUpperCase().trim()
  if (!s) throw new Error('Símbolo inválido')
  const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL as string
  const anon = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string
  if (!supabaseUrl || !anon) throw new Error('Supabase URL/Anon key não configurados')
  const session = await supabase.auth.getSession()
  const token = session.data.session?.access_token || anon
  const q: string[] = [`symbol=${encodeURIComponent(s)}`]
  if (params.preset) q.push(`preset=${encodeURIComponent(params.preset)}`)
  if (typeof params.target === 'number') q.push(`target=${Math.max(100, Math.round(params.target))}`)
  if (typeof params.cap === 'number') q.push(`cap=${Math.max(1000, Math.round(params.cap))}`)
  const url = `${supabaseUrl.replace(/\/$/, '')}/functions/v1/bootstrap-fill-5m?${q.join('&')}`
  const res = await fetch(url, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'apikey': anon } })
  if (!res.ok) throw new Error(`bootstrap-fill-5m falhou: ${res.status}`)
  const payload: any = await res.json()
  const pct = typeof payload?.percent === 'number' ? payload.percent : 0
  return Math.max(0, Math.min(100, Math.round(pct)))
}

// Novo: coleta direta por timeframe (Bybit) até completar ~500 candles desse TF.
export async function bootstrapFillTimeframeDirect(params: { symbol: string; timeframe: string; target?: number }): Promise<{ inserted: number; afterCount: number }> {
  const s = params.symbol?.toUpperCase().trim()
  const tf = String(params.timeframe).toUpperCase()
  if (!s || !tf) throw new Error('Parâmetros inválidos')
  const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL as string
  const anon = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string
  const session = await supabase.auth.getSession()
  const token = session.data.session?.access_token || anon
  const url = `${supabaseUrl.replace(/\/$/, '')}/functions/v1/bootstrap-fill-ohlcv`
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}`, 'apikey': anon, 'Content-Type': 'application/json' },
    body: JSON.stringify({ symbol: s, timeframe: tf, target: Number(params.target ?? 500) })
  })
  const payload: any = await res.json().catch(()=> null)
  if (!res.ok) {
    const msg = payload?.error || `HTTP ${res.status}`
    throw new Error(`bootstrap-fill-ohlcv falhou: ${msg}`)
  }
  
  return { inserted: Number(payload?.inserted ?? 0), afterCount: Number(payload?.afterCount ?? 0) }
}

export async function fastBootstrapSymbol(symbol: string, backBuckets: number = 300): Promise<any> {
  // Mantido aqui para coesão do fluxo, reexportando a RPC
  const { data, error } = await supabase.rpc('fast_bootstrap_symbol', {
    p_symbol: symbol,
    p_back_buckets: backBuckets,
  })
  if (error) throw error
  return data
}

export function targetForTimeframe(tf: string): number {
  const t = String(tf).toUpperCase()
  // Janela alvo por TF alinhada à função de saúde (W=60, M=24)
  if (t === 'W') return 60
  if (t === 'M') return 24
  // Para os demais TFs operacionais, manter 500 (janela deslizante plena)
  return 500
}

export async function runQuickBootstrap(
  symbol: string,
  opts: { target?: number; preset?: BootPreset; cap?: number; backBuckets?: number; onProgress?: (p: number) => void; onStart?: (tf: string, idx: number, total: number) => void; onStep?: (tf: string, idx: number, total: number) => void } = {},
): Promise<void> {
  const { target = 500, preset = 'quick', backBuckets = 300, onProgress, onStart, onStep } = opts
  // Pesos de progresso: 20% seed 5m + 80% distribuídos pelos TFs
  const seedWeight = 20
  // Excluir 'M' por padrão (opcional na UI se necessário)
  const timeframes = ['15','30','60','240','720','D','W','M']
  const total = timeframes.length
  const failures: Array<{ tf: string; error: string }> = []
  // 1) Seed 5m leve o suficiente para basear 240m; TFs maiores serão preenchidos diretamente
  try {
    onStart?.('SEED-5m', 0, total + 1)
    // Mostra progresso inicial imediatamente para evitar sensacao de travamento
    onProgress?.(5)
    await bootstrapFill5m({ symbol, preset, cap: 5000 })
    onProgress?.(seedWeight)
  } catch {}
  for (let i=0;i<total;i++) {
    const tf = timeframes[i]
    onStart?.(tf, i+1, total)
    try {
      const tfTarget = targetForTimeframe(tf)
      await bootstrapFillTimeframeDirect({ symbol, timeframe: tf, target: tfTarget })
    } catch (e:any) { failures.push({ tf, error: e?.message || String(e) }) }
    onStep?.(tf, i+1, total)
    const tfPct = Math.round((80 / total) * (i+1))
    onProgress?.(Math.min(100, seedWeight + tfPct))
    await new Promise(r=>setTimeout(r, 800))
  }
  // Dispara o runner em background para consumo regular
  try {
    const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL as string
    const anon = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string
    const session = await supabase.auth.getSession()
    const token = session.data.session?.access_token || anon
    fetch(`${supabaseUrl.replace(/\/$/, '')}/functions/v1/ohlcv-queue-runner`, {
      method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'apikey': anon, 'Content-Type': 'application/json' },
      body: JSON.stringify({ limit: 120, budget: 59000 })
    }).catch(()=>{})
  } catch {}
  if (failures.length) {
    const msg = failures.slice(0,3).map(f=> `${f.tf}: ${f.error}`).join(' | ')
    throw new Error(`Falhas em ${failures.length} TF(s): ${msg}`)
  }
}

// Força 500 candles para um timeframe específico (deriva a partir do 5m)
export async function runBootstrapForTimeframe(
  symbol: string,
  timeframe: '5'|'15'|'30'|'60'|'240'|'720'|'D'|'W'|'M',
  opts: { cap?: number; onProgress?: (p:number)=>void } = {}
): Promise<{ inserted: number; afterCount: number }> {
  const { onProgress } = opts
  // Importante: na ação "Forçar janela" o usuário espera a janela plena (500)
  // inclusive para W. Mantemos a régua de saúde (W=60) inalterada, mas aqui
  // priorizamos preencher o máximo possível quando a ação é manual.
  const tfTarget = timeframe === 'W' ? 500 : targetForTimeframe(timeframe)
  const result = await bootstrapFillTimeframeDirect({ symbol, timeframe, target: tfTarget })
  onProgress?.(100)
  // Dispara runner leve para processamento subsequente
  try {
    const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL as string
    const anon = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string
    const session = await supabase.auth.getSession()
    const token = session.data.session?.access_token || anon
    fetch(`${supabaseUrl.replace(/\/$/, '')}/functions/v1/ohlcv-queue-runner`, {
      method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'apikey': anon, 'Content-Type': 'application/json' },
      body: JSON.stringify({ limit: 96, budget: 59000 })
    }).catch(()=>{})
  } catch {}
  return result
}

// Nova: normalização em lote (5m) com derivação automática pelo backend
export async function bulkNormalize5mSymbols(
  symbols: string[],
  opts: { concurrency?: number; onProgress?: (pct: number, current?: string) => void } = {}
): Promise<void> {
  const { concurrency = 2, onProgress } = opts
  const list = Array.from(new Set(symbols.map(s => s.toUpperCase().trim()).filter(Boolean)))
  let done = 0
  const next = async () => {
    while (list.length) {
      const sym = list.shift() as string
      try {
        await bootstrapFill5m({ symbol: sym, target: 500, preset: 'quick' })
      } catch {}
      done++
      const pct = Math.min(100, Math.round((done / (done + list.length)) * 100))
      onProgress?.(pct, sym)
    }
  }
  const workers = new Array(Math.max(1, Math.min(concurrency, 6))).fill(0).map(() => next())
  await Promise.all(workers)
}

// Admin: zera a fila de buckets (ohlcv_backfill com bucket_open_time não nulo)
export async function pruneExecutionQueueBuckets(params: { timeframe?: string; symbol?: string } = {}): Promise<{ deleted: number }> {
  const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL as string
  const anon = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string
  if (!supabaseUrl || !anon) throw new Error('Supabase URL/Anon key não configurados')
  const session = await supabase.auth.getSession()
  const token = session.data.session?.access_token || anon
  const qs: string[] = []
  if (params.timeframe) qs.push(`timeframe=${encodeURIComponent(String(params.timeframe).toUpperCase())}`)
  if (params.symbol) qs.push(`symbol=${encodeURIComponent(String(params.symbol).toUpperCase())}`)
  const url = `${supabaseUrl.replace(/\/$/, '')}/functions/v1/execq-prune${qs.length ? `?${qs.join('&')}` : ''}`
  const res = await fetch(url, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, apikey: anon } })
  if (!res.ok) throw new Error(await res.text())
  const payload: any = await res.json().catch(() => null)
  return { deleted: Number(payload?.deleted ?? 0) }
}

